/** Small presentational building blocks of the mobile slip card. */
import { LOGO_URL } from "@/constants/brand";
import BrandLogo from "@/components/BrandLogo";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { LogIn, LogOut } from "lucide-react";

export const WHATSAPP_SUPPORT_NUMBER = "919690181181";
export const WHATSAPP_DISPLAY = "+91 9690 181 181";
export const SITE_URL = "www.animalmela.com";

export const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-black/60 mt-4 mb-1.5">
    {Icon ? <Icon className="w-3 h-3" /> : null}
    {children}
  </div>
);

export const KV = ({ label, value, mono = false }) => (
  <div className="flex items-baseline gap-2 py-1 border-b border-dashed border-black/10 last:border-0">
    <div className="text-[10px] uppercase tracking-widest text-black/50 font-bold w-20 shrink-0">{label}</div>
    <div className={`text-[13px] font-semibold text-black flex-1 break-words ${mono ? "font-mono" : ""}`}>
      {value || "—"}
    </div>
  </div>
);

export const Chip = ({ tone = "default", children }) => {
  const tones = {
    ok: "bg-emerald-100 text-emerald-800 border-emerald-200",
    warn: "bg-amber-100 text-amber-800 border-amber-200",
    default: "bg-black/5 text-black/70 border-black/10",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${tones[tone] || tones.default}`}>
      {children}
    </span>
  );
};

export function SlipHeader({ haatName, registrationNumber, dateStr, timeStr }) {
  return (
    <>
      <div className="flex items-center gap-3 pb-3 border-b border-black/10">
        <BrandLogo size="slip" ring={false} alt="" className="ring-1 ring-black/10" />
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-black/50">Powered by AnimalMela</div>
          {/* Full name always visible — wraps to multiple lines, never truncated */}
          <div className="text-base font-extrabold leading-tight break-words" data-testid="slip-haat-name">{haatName}</div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-widest font-bold text-black/50">Reg. No.</div>
          <div className="font-mono font-bold text-xs">{registrationNumber || "—"}</div>
        </div>
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px] text-black/60">
        <span>{dateStr}</span>
        <span className="font-mono">{timeStr}</span>
      </div>
    </>
  );
}

export function SlipFooter() {
  return (
    <div className="mt-4 pt-3 border-t border-black/10 text-center space-y-1">
      <div className="flex items-center justify-center gap-1.5">
        <img src={LOGO_URL} alt="" width={16} height={16} className="w-4 h-4 rounded-md object-cover" />
        <span className="font-display font-extrabold text-sm">AnimalMela</span>
      </div>
      <div className="text-[11px] text-black/60">{SITE_URL}</div>
      <a
        href={`https://wa.me/${WHATSAPP_SUPPORT_NUMBER}`}
        target="_blank"
        rel="noreferrer"
        className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold text-black/80"
      >
        <WhatsAppIcon className="w-4 h-4" color="#25D366" />
        WhatsApp Support · {WHATSAPP_DISPLAY}
      </a>
    </div>
  );
}

export function GatePassPanel({ isExitVariant, passLabel, passNumber, qr, animalId, entryStatus, exitStatus }) {
  return (
    <div className={`rounded-2xl border-2 ${isExitVariant ? "border-amber-600 bg-amber-50/60" : "border-black bg-white"} p-3`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2.5 py-0.5 rounded-full ${isExitVariant ? "bg-amber-600 text-white" : "bg-black text-white"}`}>{passLabel}</span>
        <span className="font-mono font-extrabold text-sm">{passNumber || "—"}</span>
      </div>
      <div className="grid place-items-center py-1">
        {qr ? (
          <img src={qr} alt="Gate Pass QR" style={{ width: "240px", height: "240px" }} />
        ) : (
          <div style={{ width: "240px", height: "240px" }} className="bg-black/5 grid place-items-center text-black/40 text-xs">QR loading…</div>
        )}
      </div>
      <div className="mt-2 text-center text-[10px] uppercase tracking-widest font-bold text-black/60">Scan at the gate</div>

      <div className="mt-2 space-y-0">
        <KV label="Animal ID" value={animalId} mono />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px]">
          <LogIn className="w-3.5 h-3.5" />
          <span className="font-bold">Entry</span>
          <Chip tone={entryStatus === "scanned" ? "ok" : "default"}>{entryStatus}</Chip>
        </div>
        <div className="flex items-center gap-1.5 text-[11px]">
          <LogOut className="w-3.5 h-3.5" />
          <span className="font-bold">Exit</span>
          <Chip tone={exitStatus === "sold" ? "ok" : exitStatus === "unsold" ? "warn" : "default"}>{exitStatus}</Chip>
        </div>
      </div>
    </div>
  );
}
